import { PermissionsMapType, UserPermissionType } from '../../../types/common/PermissionType';

export const PERMISSION_ACTIONS = {
  PUBLIC: 'public',
  READLIST: 'readList',
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DOWNLOAD_EXCEL: 'downloadExcel',
  DELETE: 'delete',
} as const;

export const PERMISSION_SUBJECTS = {
  MESSAGE: 'message',
  SELLER: 'seller',
  USER: 'user',
  USER_RANK: 'userRank',
  STORE: 'store',
  STORE_TIER: 'storeTier',
  STORE_EXPOSE: 'storeExpose',
  PRODUCT: 'product',
  ORDER: 'order',
  ORDER_RETURN: 'orderReturn',
  MULTI_CHANNEL_ORDER_ITEM: 'multiChannelOrderItem',
  BALANCE: 'balance',
  STORE_BALANCE: 'storeBalance',
  SETTLEMENT_DATE_ADJUSTMENT: 'settlementDateAdjustment',
  SETTLEMENT_COMPLETE_DATE_ADJUSTMENT: 'settlementCompleteDateAdjustment',
  PAYMENT_PG_ROUTE: 'paymentPgRoute',
  PAYMENT_PG_FEE: 'paymentPgFee',
  TAG: 'tag',
  TAG_GROUP: 'tagGroup',
  TAG_TAB_MAIN: 'tagTabMain',
  TAG_TREND_KEYWORD: 'tagTrendKeyword',
  MAIN_PAGE: 'mainPage',
  SUB_PAGE: 'subPage',
  WIDGET: 'widget',
  MENU: 'menu',
  PRODUCT_CATEGORY: 'productCategory',
  PRODUCT_CATEGORY_EXHIBITION: 'productCategoryExhibition',
  REMOTE_SPLASH_IMAGE: 'remoteSplashImage',
  REMOTE_REFERRAL_IMAGE: 'remoteReferralImage',
  PROMOTION_SCHEDULE: 'promotionSchedule',
  EVENT: 'event',
  COUPON: 'coupon',
  ALIMTALK_SEND_SCHEDULE: 'alimtalkSendSchedule',
  ALIMTALK_TEMPLATE: 'alimtalkTemplate',
  POINT: 'point',
  STORE_NOTICE: 'storeNotice',
  PRODUCT_NOTICE: 'productNotice',
  SEARCH_KEYWORD: 'searchKeyword',
  // 검색 사전(동의어·사용자사전) 편집 (AMZUWEB-694 · 백엔드 AMZBM-113).
  // 저장 즉시 고객 검색 결과가 바뀌어 검색 키워드 관리와 분리된 전용 권한이다 — 백엔드 CASL subject 와 같은 문자열.
  SEARCH_DICTIONARY: 'searchDictionary',
  // 서버 피처 플래그 켜기 · 끄기 (백엔드 AMZBM-136). 저장 즉시 그 환경의 서버 전체 동작이 바뀌어(출시 · 긴급 OFF)
  // 다른 권한과 분리된 전용 권한이다 — 백엔드 CASL subject 와 같은 문자열.
  FEATURE_FLAG: 'featureFlag',
  ONE_ON_ONE_INQUIRY: 'oneOnOneInquiry',
  PRODUCT_INQUIRY: 'productInquiry',
  REVIEW: 'review',
  VERSION: 'version',
  RECOMMEND_PRODUCT: 'recommendProduct',
  RECOMMEND_PRODUCT_REVIEW: 'recommendProductReview',
  RECOMMEND_TAG: 'recommendTag',
  RECOMMEND_SEARCH: 'recommendSearch',
  RECOMMEND_EVENT: 'recommendEvent',
  RECOMMEND_STORE: 'recommendStore',
} as const;

export const PERMISSIONS_MAP: PermissionsMapType = {
  MESSAGE_MANAGE: [PERMISSION_SUBJECTS.MESSAGE],
  ACCOUNT_MANAGE: [PERMISSION_SUBJECTS.SELLER, PERMISSION_SUBJECTS.USER, PERMISSION_SUBJECTS.USER_RANK],
  BRAND_MANAGE: [PERMISSION_SUBJECTS.STORE, PERMISSION_SUBJECTS.STORE_TIER, PERMISSION_SUBJECTS.STORE_EXPOSE],
  PRODUCT_MANAGE: [PERMISSION_SUBJECTS.PRODUCT],
  ORDER_MANAGE: [PERMISSION_SUBJECTS.ORDER, PERMISSION_SUBJECTS.ORDER_RETURN],
  MULTI_CHANNEL_MANAGE: [PERMISSION_SUBJECTS.MULTI_CHANNEL_ORDER_ITEM],
  BALANCE_MANAGE: [
    PERMISSION_SUBJECTS.BALANCE,
    PERMISSION_SUBJECTS.STORE_BALANCE,
    PERMISSION_SUBJECTS.SETTLEMENT_DATE_ADJUSTMENT,
    PERMISSION_SUBJECTS.SETTLEMENT_COMPLETE_DATE_ADJUSTMENT,
  ],
  PAYMENT_MANAGE: [PERMISSION_SUBJECTS.PAYMENT_PG_ROUTE, PERMISSION_SUBJECTS.PAYMENT_PG_FEE],
  TAG_MANAGE: [
    PERMISSION_SUBJECTS.TAG,
    PERMISSION_SUBJECTS.TAG_GROUP,
    PERMISSION_SUBJECTS.TAG_TAB_MAIN,
    PERMISSION_SUBJECTS.TAG_TREND_KEYWORD,
  ],
  PAGE_MANAGE: [
    PERMISSION_SUBJECTS.MAIN_PAGE,
    PERMISSION_SUBJECTS.SUB_PAGE,
    PERMISSION_SUBJECTS.WIDGET,
    PERMISSION_SUBJECTS.MENU,
  ],
  CATEGORY_MANAGE: [PERMISSION_SUBJECTS.PRODUCT_CATEGORY, PERMISSION_SUBJECTS.PRODUCT_CATEGORY_EXHIBITION],
  IMAGE_MANAGE: [PERMISSION_SUBJECTS.REMOTE_SPLASH_IMAGE, PERMISSION_SUBJECTS.REMOTE_REFERRAL_IMAGE],
  PROMOTION_MANAGE: [PERMISSION_SUBJECTS.PROMOTION_SCHEDULE],
  EVENT_MANAGE: [PERMISSION_SUBJECTS.EVENT],
  COUPON_MANAGE: [PERMISSION_SUBJECTS.COUPON],
  ALIMTALK_MANAGE: [PERMISSION_SUBJECTS.ALIMTALK_SEND_SCHEDULE, PERMISSION_SUBJECTS.ALIMTALK_TEMPLATE],
  POINT_MANAGE: [PERMISSION_SUBJECTS.POINT],
  NOTICE_MANAGE: [PERMISSION_SUBJECTS.STORE_NOTICE, PERMISSION_SUBJECTS.PRODUCT_NOTICE],
  KEYWORD_MANAGE: [PERMISSION_SUBJECTS.SEARCH_KEYWORD],
  // 검색 키워드와 다른 상위 줄 — 한 묶음이면 「검색 키워드」 체크가 사전 편집 권한까지 켠다
  SEARCH_DICTIONARY_MANAGE: [PERMISSION_SUBJECTS.SEARCH_DICTIONARY],
  SYSTEM_MANAGE: [PERMISSION_SUBJECTS.FEATURE_FLAG],
  INQUIRY_MANAGE: [PERMISSION_SUBJECTS.ONE_ON_ONE_INQUIRY, PERMISSION_SUBJECTS.PRODUCT_INQUIRY],
  REVIEW_MANAGE: [PERMISSION_SUBJECTS.REVIEW],
  VERSION_MANAGE: [PERMISSION_SUBJECTS.VERSION],
  RECOMMEND_MANAGE: [
    PERMISSION_SUBJECTS.RECOMMEND_PRODUCT,
    PERMISSION_SUBJECTS.RECOMMEND_PRODUCT_REVIEW,
    PERMISSION_SUBJECTS.RECOMMEND_TAG,
    PERMISSION_SUBJECTS.RECOMMEND_SEARCH,
    PERMISSION_SUBJECTS.RECOMMEND_EVENT,
    PERMISSION_SUBJECTS.RECOMMEND_STORE,
  ],
};

export const USER_INITIAL_PERMISSION: UserPermissionType = {
  [PERMISSION_SUBJECTS.MESSAGE]: {
    [PERMISSION_ACTIONS.CREATE]: false,
  },
  [PERMISSION_SUBJECTS.SELLER]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.DOWNLOAD_EXCEL]: false,
  },
  [PERMISSION_SUBJECTS.USER]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.DOWNLOAD_EXCEL]: false,
  },
  [PERMISSION_SUBJECTS.USER_RANK]: {
    [PERMISSION_ACTIONS.READLIST]: false,
  },
  [PERMISSION_SUBJECTS.STORE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.DOWNLOAD_EXCEL]: false,
  },
  [PERMISSION_SUBJECTS.STORE_TIER]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.STORE_EXPOSE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.PRODUCT]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.DELETE]: false,
    [PERMISSION_ACTIONS.DOWNLOAD_EXCEL]: false,
  },
  [PERMISSION_SUBJECTS.ORDER]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.DOWNLOAD_EXCEL]: false,
  },
  [PERMISSION_SUBJECTS.ORDER_RETURN]: {
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.MULTI_CHANNEL_ORDER_ITEM]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.DOWNLOAD_EXCEL]: false,
  },
  [PERMISSION_SUBJECTS.BALANCE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.DOWNLOAD_EXCEL]: false,
  },
  [PERMISSION_SUBJECTS.STORE_BALANCE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.DOWNLOAD_EXCEL]: false,
  },
  [PERMISSION_SUBJECTS.SETTLEMENT_DATE_ADJUSTMENT]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.SETTLEMENT_COMPLETE_DATE_ADJUSTMENT]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.PAYMENT_PG_ROUTE]: {
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.PAYMENT_PG_FEE]: {
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.TAG]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.TAG_GROUP]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.TAG_TAB_MAIN]: {
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.TAG_TREND_KEYWORD]: {
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.WIDGET]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.DELETE]: false,
  },
  [PERMISSION_SUBJECTS.MAIN_PAGE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.SUB_PAGE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.DELETE]: false,
  },
  [PERMISSION_SUBJECTS.MENU]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
  },
  [PERMISSION_SUBJECTS.PRODUCT_CATEGORY]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.DELETE]: false,
  },
  [PERMISSION_SUBJECTS.PRODUCT_CATEGORY_EXHIBITION]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.REMOTE_REFERRAL_IMAGE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.REMOTE_SPLASH_IMAGE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  // 프로모션 스케줄 — 편집 주체는 MD·마케팅. 백엔드 CASL: readList=타임라인+변경 이력 조회, create/update/delete=항목 편집
  // 이력 조회는 readList 에 통합했다 (상품 관리처럼 조회 권한이 있으면 이력도 본다)
  [PERMISSION_SUBJECTS.PROMOTION_SCHEDULE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.DELETE]: false,
    [PERMISSION_ACTIONS.DOWNLOAD_EXCEL]: false,
  },
  [PERMISSION_SUBJECTS.EVENT]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.COUPON]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  // 알림톡 대량 발송 — 대량 외부 발송 기능이라 4 액션 모두 기본 false (백엔드 CASL: create=등록/테스트/upload-url, read=상세/preview, update=취소)
  [PERMISSION_SUBJECTS.ALIMTALK_SEND_SCHEDULE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.ALIMTALK_TEMPLATE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.POINT]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
  },
  [PERMISSION_SUBJECTS.PRODUCT_NOTICE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.DELETE]: false,
  },
  [PERMISSION_SUBJECTS.STORE_NOTICE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.DELETE]: false,
  },
  [PERMISSION_SUBJECTS.SEARCH_KEYWORD]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.SEARCH_DICTIONARY]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.DELETE]: false,
  },
  [PERMISSION_SUBJECTS.FEATURE_FLAG]: {
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.ONE_ON_ONE_INQUIRY]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
  },
  [PERMISSION_SUBJECTS.PRODUCT_INQUIRY]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.REVIEW]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
    [PERMISSION_ACTIONS.DOWNLOAD_EXCEL]: false,
  },
  [PERMISSION_SUBJECTS.VERSION]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
    [PERMISSION_ACTIONS.UPDATE]: false,
  },
  [PERMISSION_SUBJECTS.RECOMMEND_PRODUCT]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
  },
  [PERMISSION_SUBJECTS.RECOMMEND_PRODUCT_REVIEW]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
  },
  [PERMISSION_SUBJECTS.RECOMMEND_TAG]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
  },
  [PERMISSION_SUBJECTS.RECOMMEND_SEARCH]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
  },
  [PERMISSION_SUBJECTS.RECOMMEND_EVENT]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
  },
  [PERMISSION_SUBJECTS.RECOMMEND_STORE]: {
    [PERMISSION_ACTIONS.READLIST]: false,
    [PERMISSION_ACTIONS.READ]: false,
    [PERMISSION_ACTIONS.CREATE]: false,
  },
};
